import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import GoogleMapReact from 'google-map-react'
import MapPoint from './MapPoint'
import {
  Crosshair,
  Path,
  MapPin,
  Warning,
  CheckCircle,
  Info,
  CircleDashed,
  CaretRight,
  CaretDown
} from '@phosphor-icons/react'
import { Collapse, Tooltip } from '@mui/material'
import { useReducedMotion } from 'framer-motion'
import { distanceBetweenMarkers } from './helpers/mapGeometry'
import { MapModalStore } from '../hook/MapModalProvider'
import { API_KEY } from '../../constants/constant'

const DEFAULT_CENTER = {
  lat: 8.986984,
  lng: -79.518519
}

const GEOFENCE_CIRCLE_STYLE = {
  strokeColor: '#7F7F7F',
  strokeOpacity: 0.4,
  strokeWeight: 2,
  fillColor: '#7F7F7F',
  fillOpacity: 0.3
}

const parseCoordinate = (value) => {
  const parsedValue = Number.parseFloat(value)
  return Number.isFinite(parsedValue) ? parsedValue : 0
}

const isValidCoordinatePair = (latitude, longitude) =>
  parseCoordinate(latitude) !== 0 && parseCoordinate(longitude) !== 0

const arePlacesVeryClose = (places) => {
  if (places.length < 2) return false

  let minLatitude = places[0].lat
  let maxLatitude = places[0].lat
  let minLongitude = places[0].lng
  let maxLongitude = places[0].lng

  places.forEach((place) => {
    minLatitude = Math.min(minLatitude, place.lat)
    maxLatitude = Math.max(maxLatitude, place.lat)
    minLongitude = Math.min(minLongitude, place.lng)
    maxLongitude = Math.max(maxLongitude, place.lng)
  })

  return maxLatitude - minLatitude < 0.00045 && maxLongitude - minLongitude < 0.00045
}

export default function ReportMapCanvas({
  markers = [],
  geofence = null,
  isMobileView = false,
  defaultCenter = DEFAULT_CENTER,
  defaultZoom = 16,
  maxZoom,
  singlePointZoom,
  fitBoundsPadding,
  bindResize = false,
  closePlacesMinZoom = null,
  modern = false,
  captureKind = 'activity',
  geofenceInfo = null,
  geofenceStatus = 'unknown'
}) {
  const reduceMotion = useReducedMotion()
  const [geofenceFocus, setGeofenceFocus] = useState(0)
  const circleRef = useRef(null)
  const cameraFrameRef = useRef(null)
  const [focusedGeofence, setFocusedGeofence] = useState(false)
  const panelRef = useRef(null)
  const [panelExpanded, setPanelExpanded] = useState(!isMobileView)
  const [mapReady, setMapReady] = useState(false)
  const [api, setApi] = useState(null)
  const [showConnection, setShowConnection] = useState(true)
  const [focusedMarker, setFocusedMarker] = useState(null)
  const { modalMapStyles } = useContext(MapModalStore)

  const validMarkers = useMemo(
    () => (markers || []).filter((marker) => isValidCoordinatePair(marker.lat, marker.lng)),
    [markers]
  )

  const resolvedGeofence = useMemo(() => {
    if (!geofence || !isValidCoordinatePair(geofence.lat, geofence.lng)) return null

    return {
      lat: parseCoordinate(geofence.lat),
      lng: parseCoordinate(geofence.lng),
      radius: Number.parseInt(geofence.radius, 10) || 100
    }
  }, [geofence])

  const resolvedDefaultCenter = useMemo(() => {
    if (defaultCenter && isValidCoordinatePair(defaultCenter.lat, defaultCenter.lng)) {
      return {
        lat: parseCoordinate(defaultCenter.lat),
        lng: parseCoordinate(defaultCenter.lng)
      }
    }

    if (validMarkers[0]) {
      return {
        lat: parseCoordinate(validMarkers[0].lat),
        lng: parseCoordinate(validMarkers[0].lng)
      }
    }

    return DEFAULT_CENTER
  }, [defaultCenter, validMarkers])

  const resolvedMaxZoom = maxZoom ?? (modern ? 21 : isMobileView ? 19 : 18)
  const resolvedSinglePointZoom = singlePointZoom ?? (isMobileView ? 17 : 16)
  const resolvedFitBoundsPadding =
    fitBoundsPadding === undefined ? (isMobileView ? 24 : 84) : fitBoundsPadding

  const markerSignature = validMarkers
    .map((marker) => `${marker.lat},${marker.lng},${marker.color}`)
    .join('|')
  const geofenceSignature = resolvedGeofence
    ? `${resolvedGeofence.lat},${resolvedGeofence.lng},${resolvedGeofence.radius}`
    : ''

  const getMapBounds = (maps, places) => {
    const bounds = new maps.LatLngBounds()
    places.forEach((place) => {
      bounds.extend(new maps.LatLng(place.lat, place.lng))
    })
    return bounds
  }

  const fitBounds = (map, bounds) => {
    if (resolvedFitBoundsPadding == null) {
      map.fitBounds(bounds)
      return
    }

    map.fitBounds(
      bounds,
      modern
        ? isMobileView
          ? { top: 45, right: 35, bottom: (panelRef.current?.offsetHeight || 110) + 45, left: 35 }
          : { top: 70, right: 70, bottom: 55, left: 365 }
        : resolvedFitBoundsPadding
    )
  }

  const fitMapToPlaces = (map, maps) => {
    const places = validMarkers.map((marker) => ({
      lat: parseCoordinate(marker.lat),
      lng: parseCoordinate(marker.lng)
    }))

    if (places.length <= 1) {
      map.setCenter(places[0] || resolvedDefaultCenter)
      map.setZoom(resolvedSinglePointZoom)
      return
    }

    const bounds = getMapBounds(maps, places)
    fitBounds(map, bounds)

    if (closePlacesMinZoom && arePlacesVeryClose(places)) {
      const currentZoom = map.getZoom()
      if (typeof currentZoom === 'number' && currentZoom < closePlacesMinZoom) {
        map.setZoom(closePlacesMinZoom)
      }
    }
  }

  useEffect(() => {
    if (!api) return undefined
    const { map, maps } = api
    setFocusedMarker(null)
    const idle = maps.event.addListener(map, 'idle', () => setMapReady(true))
    if (map.getBounds?.()) setMapReady(true)
    const drag = maps.event.addListener?.(map, 'dragstart', () =>
      window.cancelAnimationFrame(cameraFrameRef.current)
    )
    fitMapToPlaces(map, maps)
    const circle = resolvedGeofence
      ? new maps.Circle({
          ...GEOFENCE_CIRCLE_STYLE,
          ...(modern
            ? {
                strokeColor:
                  geofenceStatus === 'outside'
                    ? '#e88743'
                    : geofenceStatus === 'inside'
                      ? '#29976b'
                      : '#7F7F7F',
                strokeOpacity: 0.9,
                strokeWeight: 2,
                fillColor:
                  geofenceStatus === 'outside'
                    ? '#f5a76c'
                    : geofenceStatus === 'inside'
                      ? '#62bb92'
                      : '#7F7F7F',
                fillOpacity: 0.2
              }
            : {}),
          map,
          center: { lat: resolvedGeofence.lat, lng: resolvedGeofence.lng },
          radius: resolvedGeofence.radius
        })
      : null
    circleRef.current = circle
    const resize = () => fitMapToPlaces(map, maps)
    if (bindResize) window.addEventListener('resize', resize)
    return () => {
      window.cancelAnimationFrame(cameraFrameRef.current)
      maps.event.removeListener(idle)
      if (drag) maps.event.removeListener(drag)
      circle?.setMap(null)
      circleRef.current = null
      window.removeEventListener('resize', resize)
    }
  }, [api, markerSignature, geofenceSignature, isMobileView, modern, bindResize, geofenceStatus])

  const connectionPairs = useMemo(() => {
    if (captureKind !== 'timestamp') return validMarkers.length === 2 ? [validMarkers] : []
    const client = validMarkers.find((marker) => marker.isReport)
    return client
      ? validMarkers
          .filter((marker) => !marker.isReport)
          .map((marker) => (marker.controlLabel === 'Fin' ? [client, marker] : [marker, client]))
      : []
  }, [validMarkers, captureKind])

  useEffect(() => {
    if (!api || !modern || !showConnection || !connectionPairs.length) return undefined
    const lines = connectionPairs.map(
      (pair) =>
        new api.maps.Polyline({
          map: api.map,
          path: pair.map(({ lat, lng }) => ({
            lat: parseCoordinate(lat),
            lng: parseCoordinate(lng)
          })),
          geodesic: true,
          strokeOpacity: 0,
          clickable: false,
          icons: [
            {
              icon: {
                path: captureKind === 'timestamp' ? 'M -1,-1 0,1 1,-1' : 'M 0,-1 0,1',
                strokeColor: pair.find((marker) => !marker.isReport)?.controlColor || '#477cad',
                strokeOpacity: 0.85,
                scale: 2
              },
              offset: '0',
              repeat: '14px'
            }
          ]
        })
    )
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)')
    let timer
    let offset = 0
    const updateMotion = () => {
      window.clearInterval(timer)
      if (!preference.matches)
        timer = window.setInterval(() => {
          offset = (offset + 1) % 14
          lines.forEach((line) => {
            const icons = line.get('icons')
            icons[0].offset = `${offset}px`
            line.set('icons', icons)
          })
        }, 90)
    }
    updateMotion()
    preference.addEventListener('change', updateMotion)
    return () => {
      window.clearInterval(timer)
      preference.removeEventListener('change', updateMotion)
      lines.forEach((line) => line.setMap(null))
    }
  }, [api, modern, showConnection, connectionPairs, captureKind])

  // Briefly illuminate the actual boundary after the camera settles.
  useEffect(() => {
    if (!api || !geofenceFocus || reduceMotion) return undefined
    const circle = circleRef.current
    if (!circle) return undefined
    let frame
    let startedAt
    const pulse = (now) => {
      startedAt ??= now
      const progress = Math.min((now - startedAt) / 850, 1)
      const glow = Math.sin(progress * Math.PI)
      circle.setOptions({ strokeWeight: 2 + glow * 2, fillOpacity: 0.2 + glow * 0.12 })
      if (progress < 1) frame = window.requestAnimationFrame(pulse)
    }
    const listener = api.maps.event.addListenerOnce(api.map, 'idle', () => {
      frame = window.requestAnimationFrame(pulse)
    })
    return () => {
      api.maps.event.removeListener(listener)
      window.cancelAnimationFrame(frame)
      circle.setOptions({ strokeWeight: 2, fillOpacity: 0.2 })
    }
  }, [api, geofenceFocus, reduceMotion, geofenceSignature])

  // Cancel a previous flight when another location is selected or the map closes.
  const stopCamera = () => window.cancelAnimationFrame(cameraFrameRef.current)
  const flyToPoint = (center, zoom) => {
    stopCamera()
    const map = api.map
    const origin = map.getCenter?.()
    const startZoom = map.getZoom?.()
    if (reduceMotion || !origin || !Number.isFinite(startZoom)) {
      map.setCenter(center)
      map.setZoom(zoom)
      return
    }
    const start = { lat: origin.lat(), lng: origin.lng() }
    const longitudeDelta = ((center.lng - start.lng + 540) % 360) - 180
    const fractionalZoom = Boolean(map.get?.('isFractionalZoomEnabled'))
    const smoothStep = (value) => value * value * (3 - 2 * value)
    let startedAt
    let stepStartedAt
    let stepZoom = startZoom
    let centered = false
    const animate = (now) => {
      startedAt ??= now
      // Center first, while the map is still wide enough to follow the movement.
      // Repeated setCenter calls during native zoom would interrupt its animation.
      if (!centered) {
        const progress = Math.min((now - startedAt) / 420, 1)
        const eased = smoothStep(progress)
        map.setCenter({
          lat: start.lat + (center.lat - start.lat) * eased,
          lng: start.lng + longitudeDelta * eased
        })
        centered = progress === 1
        if (!centered) {
          cameraFrameRef.current = window.requestAnimationFrame(animate)
          return
        }
      }
      if (stepZoom === zoom) return
      const nextZoom =
        stepZoom + Math.sign(zoom - stepZoom) * Math.min(1, Math.abs(zoom - stepZoom))
      // One wheel-like step at a time, with extra settling time at the destination.
      // Start each step from now so delayed frames never skip several levels.
      const duration = nextZoom === zoom ? 480 : 340
      if (stepStartedAt === undefined) {
        stepStartedAt = now
        if (!fractionalZoom) map.setZoom(nextZoom)
      }
      const progress = Math.min((now - stepStartedAt) / duration, 1)
      if (fractionalZoom) map.setZoom(stepZoom + (nextZoom - stepZoom) * smoothStep(progress))
      if (progress === 1) {
        stepZoom = nextZoom
        stepStartedAt = undefined
      }
      if (stepZoom !== zoom) cameraFrameRef.current = window.requestAnimationFrame(animate)
    }
    cameraFrameRef.current = window.requestAnimationFrame(animate)
  }

  const focusMarker = (marker, index) => {
    if (!api) return
    const center = { lat: parseCoordinate(marker.lat), lng: parseCoordinate(marker.lng) }
    if (isMobileView) setPanelExpanded(false)
    flyToPoint(center, modern ? resolvedMaxZoom : resolvedSinglePointZoom)
    setFocusedGeofence(false)
    setFocusedMarker(index)
  }
  const distances = modern
    ? connectionPairs
        .map((pair) => ({
          label:
            captureKind === 'timestamp'
              ? pair[0].isReport
                ? 'Cliente → Fin'
                : 'Inicio → Cliente'
              : 'Distancia entre actividad y cliente',
          value: distanceBetweenMarkers(pair)
        }))
        .filter(({ value }) => value !== null)
    : []

  const focusGeofence = () => {
    const bounds = circleRef.current?.getBounds()
    if (!api || !bounds) return
    stopCamera()
    if (isMobileView) setPanelExpanded(false)
    setFocusedGeofence(true)
    setGeofenceFocus((current) => current + 1)
    api.map.fitBounds(
      bounds,
      isMobileView
        ? { top: 60, right: 30, bottom: 155, left: 30 }
        : { top: 60, right: 60, bottom: 50, left: 355 }
    )
    setFocusedMarker(null)
  }
  const status = !resolvedGeofence ? 'unknown' : geofenceStatus
  const statusTitle =
    status === 'outside'
      ? 'Fuera de geocerca'
      : status === 'inside'
        ? 'Dentro de geocerca'
        : 'Geocerca sin evaluar'
  const StatusIcon = status === 'outside' ? Warning : status === 'inside' ? CheckCircle : Info
  const renderControl = (label, icon, onClick, pressed, color) => (
    <Tooltip
      key={label}
      title={label}
      arrow
      placement="top"
      slotProps={{ popper: { sx: { zIndex: 2100 } } }}
    >
      <span>
        <button
          type="button"
          disabled={!mapReady}
          aria-label={label}
          aria-pressed={pressed}
          onClick={onClick}
          style={color ? { '--map-control-color': color } : undefined}
        >
          {icon}
        </button>
      </span>
    </Tooltip>
  )

  const mapOptions = {
    styles: modern
      ? [
          { featureType: 'poi', stylers: [{ visibility: 'off' }] },
          { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#b8dbea' }] },
          { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#edf2f5' }] },
          { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] }
        ]
      : modalMapStyles,
    clickableIcons: !isMobileView,
    fullscreenControl: !isMobileView,
    mapTypeControl: !isMobileView,
    streetViewControl: !isMobileView,
    keyboardShortcuts: !isMobileView,
    gestureHandling: isMobileView ? 'greedy' : 'auto',
    zoomControl: true
  }

  if (validMarkers.length === 0) return null

  return (
    <div
      className={`activity-map-frame ${modern ? 'report-map-explorer' : ''} ${mapReady ? 'activity-map-frame--ready' : ''}`}
    >
      <div className="activity-map-loading">
        <div className="activity-map-loading-badge">Cargando mapa</div>
        <div className="activity-map-loading-shimmer" />
      </div>
      <div className="report-map-explorer__canvas">
        <GoogleMapReact
          bootstrapURLKeys={{
            key: API_KEY
          }}
          defaultCenter={resolvedDefaultCenter}
          defaultZoom={defaultZoom}
          layerTypes={[]}
          options={mapOptions}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ map, maps }) => {
            map.setOptions({
              maxZoom: resolvedMaxZoom
            })
            setApi({ map, maps })
          }}
        >
          {validMarkers.map((marker, index) => (
            <MapPoint
              key={`${marker.color}-${marker.lat}-${marker.lng}-${index}`}
              lat={parseCoordinate(marker.lat)}
              lng={parseCoordinate(marker.lng)}
              name={marker.name}
              isReport={Boolean(marker.isReport)}
              color={marker.color}
              label={marker.controlLabel}
              pinColor={marker.controlColor}
              disableAnimation={isMobileView}
              modern={modern}
              selected={focusedMarker === index}
              onClick={() => focusMarker(marker, index)}
            />
          ))}
        </GoogleMapReact>
      </div>
      {modern && (
        <aside
          ref={panelRef}
          className={`report-map-explorer__panel report-map-explorer__panel--${status} ${captureKind === 'timestamp' ? 'report-map-explorer__panel--timestamp' : ''}`}
          aria-label="Panel de ubicaciones"
        >
          <div
            className="report-map-explorer__controls"
            role="group"
            aria-label="Explorar ubicaciones"
          >
            {validMarkers.map((marker, index) =>
              renderControl(
                marker.controlLabel || (marker.isReport ? 'Cliente' : 'Actividad'),
                <MapPin size={20} weight="fill" />,
                () => focusMarker(marker, index),
                focusedMarker === index,
                marker.controlColor || (marker.isReport ? '#279753' : '#e45050')
              )
            )}
            {distances.length > 0 &&
              renderControl(
                'Mostrar conexión en línea recta',
                <Path size={20} />,
                () => setShowConnection((current) => !current),
                showConnection
              )}
            {captureKind !== 'timestamp' && resolvedGeofence &&
              renderControl(
                'Acercar a la geocerca',
                <CircleDashed size={21} />,
                focusGeofence,
                focusedGeofence
              )}
            {renderControl(
              panelExpanded ? 'Contraer información' : 'Mostrar información',
              panelExpanded ? <CaretDown size={18} /> : <Info size={20} />,
              () => setPanelExpanded((current) => !current),
              panelExpanded
            )}
          </div>
          <Collapse in={panelExpanded} timeout={reduceMotion ? 0 : 320} unmountOnExit>
            <div className="report-map-explorer__insight">
              {captureKind === 'timestamp' && geofenceInfo?.radius && !resolvedGeofence && (
                <p className="report-map-explorer__geofence-note">
                  Cliente sin ubicación registrada; no se puede dibujar la geocerca.
                </p>
              )}
              {captureKind !== 'timestamp' && (
                <div className="report-map-explorer__status">
                  <span className="report-map-explorer__status-icon">
                    <StatusIcon size={24} />
                  </span>
                  <div>
                    <strong>{statusTitle}</strong>
                    <p>
                      {status === 'unknown'
                        ? 'No hay un estado de geocerca disponible para esta captura.'
                        : captureKind === 'timestamp'
                          ? 'Estado registrado al procesar la visita.'
                          : 'Estado registrado al procesar la actividad.'}
                    </p>
                  </div>
                </div>
              )}
              {distances.map(({ label, value: distance }) => (
                <div className="report-map-explorer__distance" key={label}>
                  <span>{label}</span>
                  <strong>
                    {distance < 1000
                      ? `${Math.round(distance)} m`
                      : `${(distance / 1000).toLocaleString('es', { maximumFractionDigits: 2 })} km`}
                  </strong>
                  {captureKind !== 'timestamp' && (
                    <small>En línea recta · calculada con coordenadas GPS</small>
                  )}
                </div>
              ))}
              {captureKind === 'timestamp' && distances.length > 0 && (
                <p className="report-map-explorer__distance-note">
                  En línea recta · coordenadas GPS
                </p>
              )}
            </div>
          </Collapse>
          <button
            className="report-map-explorer__overview"
            type="button"
            disabled={!mapReady}
            aria-label="Ver todos los puntos"
            onClick={() => {
              stopCamera()
              fitMapToPlaces(api.map, api.maps)
              setFocusedGeofence(false)
              setFocusedMarker(null)
            }}
          >
            <Crosshair size={19} />
            <span>
              {captureKind === 'timestamp' || validMarkers.length !== 2
                ? 'Ver todos los puntos'
                : 'Ver ambos puntos'}
            </span>
            <CaretRight size={16} />
          </button>
        </aside>
      )}
    </div>
  )
}
